import { z } from 'zod'
import { dayTradeBotEventSchema } from './day-trade-bot.schema'

// per-tick price + state snapshot for the day-trade debug chart. mirrors the auto-buy
// botSessionTickSchema, doubled for the two legs (buy + sell). phases are plain strings
// ("IDLE" | "WATCHING") — the snapshot stores them as strings, no phase enum exists.
export const dayTradeSessionTickSchema = z.object({
  timestamp: z.coerce.date(),
  price: z.number(),
  buyPhase: z.string(),
  sellPhase: z.string(),
  peak: z.number(),
  // price at/below which a buy arms (null when the buy leg is inactive — cap reached)
  buyArmPrice: z.number().nullable(),
  // price at/above which a sell arms (null when flat — nothing to sell)
  sellArmPrice: z.number().nullable(),
  buyLocalLow: z.number().nullable(),
  sellLocalHigh: z.number().nullable(),
  buyTicks: z.number().int(),
  sellTicks: z.number().int(),
  lotsHeld: z.number().int(),
  // running weighted average entry; 0 while flat (the chart hides the line when lotsHeld === 0)
  avgEntry: z.number(),
})

export type DayTradeSessionTickDto = z.infer<typeof dayTradeSessionTickSchema>

export const dayTradeBotDebugResponseSchema = z.object({
  ticks: z.array(dayTradeSessionTickSchema),
  events: z.array(dayTradeBotEventSchema),
})

export type DayTradeBotDebugResponseDto = z.infer<typeof dayTradeBotDebugResponseSchema>

// max range = 31 days; exceeding this is almost always a client bug and produces
// unbounded responses
const MAX_DEBUG_WINDOW_MS = 31 * 24 * 60 * 60 * 1000

export const dayTradeBotDebugQuerySchema = z
  .object({
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
  })
  .superRefine((d, ctx) => {
    if (d.from && d.to && d.from.getTime() > d.to.getTime()) {
      ctx.addIssue({
        code: 'custom',
        message: '`from` must be <= `to`',
        path: ['from'],
      })
    }
    if (d.from && d.to && d.to.getTime() - d.from.getTime() > MAX_DEBUG_WINDOW_MS) {
      ctx.addIssue({
        code: 'custom',
        message: `range must be <= ${MAX_DEBUG_WINDOW_MS / (24 * 3_600_000)}d`,
        path: ['to'],
      })
    }
  })

export type DayTradeBotDebugQueryDto = z.infer<typeof dayTradeBotDebugQuerySchema>
